// AdminAdviserMailsContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const AdminAdviserMailsContext = createContext();

const LS_KEYS = {
  LIST: "adminAdviserMails",
  CURRENT: "currentAdviserEmail",
};

const parseJSON = (s, fallback) => {
  try {
    const v = JSON.parse(s);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

export const AdminAdviserMailsProvider = ({ children }) => {
  // 1) List of adviser emails
  const [adminAdviserMails, setAdminAdviserMails] = useState(() => {
    const stored = localStorage.getItem(LS_KEYS.LIST);
    const arr = parseJSON(stored, []);
    return Array.isArray(arr) ? arr : [];
  });

  // 2) Current adviser email
  const [currentAdviserEmail, setCurrentAdviserEmail] = useState(() => {
    const stored = localStorage.getItem(LS_KEYS.CURRENT);
    return stored || ""; // empty string means "not set"
  });

  // Persist list
  useEffect(() => {
    if (adminAdviserMails.length > 0) {
      localStorage.setItem(LS_KEYS.LIST, JSON.stringify(adminAdviserMails));
    } else {
      localStorage.removeItem(LS_KEYS.LIST);
    }
  }, [adminAdviserMails]);

  // Ensure current stays valid & persist it
  useEffect(() => {
    // If current is missing or not in list, set to first item (if any)
    if (
      !currentAdviserEmail ||
      !adminAdviserMails.includes(currentAdviserEmail)
    ) {
      const fallback = adminAdviserMails[0] || "";
      setCurrentAdviserEmail(fallback);
      if (fallback) {
        localStorage.setItem(LS_KEYS.CURRENT, fallback);
      } else {
        localStorage.removeItem(LS_KEYS.CURRENT);
      }
      return;
    }
    // Persist current if valid
    localStorage.setItem(LS_KEYS.CURRENT, currentAdviserEmail);
  }, [adminAdviserMails, currentAdviserEmail]);

  // Helpers
  const addAdviser = (email) => {
    if (!email) return;
    const normalized = String(email).trim();
    if (!normalized) return;
    setAdminAdviserMails((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized]
    );
  };

  const removeAdviser = (email) => {
    setAdminAdviserMails((prev) => prev.filter((e) => e !== email));
  };

  const setCurrentAdviser = (email) => {
    const normalized = String(email || "").trim();
    if (!normalized) {
      setCurrentAdviserEmail("");
      localStorage.removeItem(LS_KEYS.CURRENT);
      return;
    }
    // If not in list, optionally add it (or bail—your choice)
    setAdminAdviserMails((prev) =>
      prev.includes(normalized) ? prev : [...prev, normalized]
    );
    setCurrentAdviserEmail(normalized);
    localStorage.setItem(LS_KEYS.CURRENT, normalized);
  };

  const clearCurrentAdviser = () => {
    setCurrentAdviserEmail("");
    localStorage.removeItem(LS_KEYS.CURRENT);
  };

  const value = useMemo(
    () => ({
      adminAdviserMails,
      setAdminAdviserMails, // raw setter if you need batch operations
      currentAdviserEmail,
      setCurrentAdviser,
      clearCurrentAdviser,
      addAdviser,
      removeAdviser,
    }),
    [adminAdviserMails, currentAdviserEmail]
  );

  return (
    <AdminAdviserMailsContext.Provider value={value}>
      {children}
    </AdminAdviserMailsContext.Provider>
  );
};

export const useAdminAdviserMails = () => useContext(AdminAdviserMailsContext);
